import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MapPin, Building2, Briefcase, Factory, ChevronRight, ExternalLink } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

// Translation maps for French
const sectorTranslations = {
  'Energy & Utilities': 'Energy & Utilities',
  'Chemicals': 'Chemicals',
  'Diversified Industries': 'Diversified Industries',
  'Food & Agriculture': 'Food & Agriculture',
  'Fertilizer': 'Fertilizer',
  'Basic Materials': 'Basic Materials',
  'Food': 'Food',
  'Beverage': 'Beverage',
  'Manufacturing': 'Manufacturing',
  'Services': 'Services',
  'Healthcare': 'Healthcare',
  'Aerospace & Defence': 'Aerospace & Defence',
  'Bio-based industries': 'Bio-based industries',
  'Energy (renewables, alternative fuels)': 'Energy (renewables, alternative fuels)',
  'Luxury': 'Luxury',
  'Process Industries': 'Process Industries'
};

const geoTranslations = {
  'France': 'France',
  'Poland': 'Pologne',
  'Germany': 'Allemagne',
  'Canada': 'Canada',
  'India': 'Inde',
  'United Kingdom': 'Royaume-Uni',
  'China': 'Chine',
  'Belgium': 'Belgique'
};

const strategyTranslations = {
  'Buyout': 'Buyout',
  'Growth': 'Growth',
  'Special Situations': 'Special Situations',
  'Venture': 'Venture'
};

const statusTranslations = {
  'In Portfolio': 'En portefeuille',
  'Partially Realised': 'Partiellement réalisé',
  'Realised': 'Réalisé'
};

// Company Data - Updated with new labels and TCEM
const companiesData = [
  {
    id: 1,
    name: 'Fapmo',
    status: 'In Portfolio',
    investor: 'Moret Industries Group',
    investmentStrategy: 'Buyout',
    geographies: ['France'],
    geographicPresence: 'Outreau (France), Coueron (France)',
    sectors: ['Aerospace & Defence', 'Energy & Utilities', 'Chemicals', 'Diversified Industries'],
    activityType: 'Manufacturing, Engineering, Services (Maintenance)',
    description: "Fapmo, founded in 1923, is a designer and manufacturer of pumps for constrained and complex environments. The company's pumps are present on submarines, surface vessels, and in energy and chemicals installations. Fapmo has unique expertise and product design capabilities for submerged rotor pumps. In 2024, Fapmo acquired the electric engine business EN Moteurs based in Coueron. EN Moteurs designs and sells electric motors which are key components in a variety of industrial processes.",
    descriptionFr: "Fapmo, fondée en 1923, est un concepteur et fabricant de pompes pour des environnements contraints et complexes. Les pompes de l'entreprise sont présentes sur des sous-marins, des navires de surface, ainsi que dans des installations énergétiques et chimiques. Fapmo possède une expertise unique et des capacités de conception de produits pour les pompes à rotor immergé. En 2024, Fapmo a acquis l'activité de moteurs électriques EN Moteurs, basée à Coueron. EN Moteurs conçoit et commercialise des moteurs électriques qui sont des composants clés dans divers processus industriels.",
    geographicPresenceFr: 'Outreau (France), Coueron (France)',
    activityTypeFr: 'Fabrication, Ingénierie, Services (Maintenance)',
    logo: 'https://customer-assets.emergentagent.com/job_4992c407-209d-4ffe-8201-36194c9609e6/artifacts/ipkxz0rt_image00001.png',
    image: 'https://customer-assets.emergentagent.com/job_69649056-df98-4ca6-bbd3-c5d20803d8d9/artifacts/lnbyeot2_image00003.jpeg',
    website: null
  },
  {
    id: 2,
    name: 'Maguin Promill',
    status: 'In Portfolio',
    investor: 'Moret Industries Group',
    investmentStrategy: 'Buyout',
    geographies: ['France', 'Poland'],
    geographicPresence: 'Charmes (France), Chelmza (Poland)',
    sectors: ['Food & Agriculture', 'Fertilizer', 'Energy & Utilities', 'Basic Materials'],
    activityType: 'Manufacturing, Engineering, Services (Maintenance)',
    description: "Maguin, founded in 1843, is a designer and manufacturer of industrial equipment for the washing, drying, shredding and extraction of sugar from beetroot. Maguin has built unique know-how and technologies for the sugar industry over close to two centuries, which has been evolving to serve a variety of sectors and industrial processes which rely on the company's capabilities in process engineering.",
    descriptionFr: "Maguin, fondée en 1843, est un concepteur et fabricant d'équipements industriels pour le lavage, le séchage, le broyage et l'extraction du sucre de betterave. Maguin a développé un savoir-faire et des technologies uniques pour l'industrie sucrière pendant près de deux siècles, et évolue désormais pour servir une variété de secteurs et de processus industriels qui s'appuient sur les compétences de l'entreprise en ingénierie des procédés.",
    geographicPresenceFr: 'Charmes (France), Chelmza (Pologne)',
    activityTypeFr: 'Fabrication, Ingénierie, Services (Maintenance)',
    logo: 'https://customer-assets.emergentagent.com/job_69649056-df98-4ca6-bbd3-c5d20803d8d9/artifacts/qygpnduq_image00002.png',
    image: 'https://customer-assets.emergentagent.com/job_69649056-df98-4ca6-bbd3-c5d20803d8d9/artifacts/6vry6vv0_image00004.jpeg',
    website: null
  },
  {
    id: 3,
    name: 'VetterTec',
    status: 'In Portfolio',
    investor: 'Moret Industries Group',
    investmentStrategy: 'Buyout',
    geographies: ['Germany', 'Canada', 'India', 'United Kingdom', 'China'],
    geographicPresence: 'Kassel, Reutlingen (Germany), Laval (Canada), Pune (India), Leatherhead (United Kingdom), Shanghai (China)',
    sectors: ['Food & Agriculture', 'Energy & Utilities', 'Food', 'Beverage', 'Chemicals'],
    activityType: 'Manufacturing, Engineering, Services (Maintenance)',
    description: "VetterTec is a one-stop supplier of industrial drying solutions, including dewatering and evaporation equipment, from R&D-scale and standalone equipment to fully integrated production lines and continuous manufacturing systems – for sophisticated liquid, powder and bulk solid production processes in the starch, food, chemical, bioethanol/alcohol, brewery and many other industries.",
    descriptionFr: "VetterTec est un fournisseur complet de solutions de séchage industriel, y compris des équipements de déshydratation et d'évaporation, allant de la R&D et des équipements autonomes à des lignes de production entièrement intégrées et des systèmes de fabrication en continu — pour des procédés de production sophistiqués de liquides, poudres et solides en vrac dans les secteurs de l'amidon, de l'alimentation, de la chimie, du bioéthanol/alcool, de la brasserie et de nombreuses autres industries.",
    geographicPresenceFr: 'Kassel, Reutlingen (Allemagne), Laval (Canada), Pune (Inde), Leatherhead (Royaume-Uni), Shanghai (Chine)',
    activityTypeFr: 'Fabrication, Ingénierie, Services (Maintenance)',
    logo: 'https://customer-assets.emergentagent.com/job_4992c407-209d-4ffe-8201-36194c9609e6/artifacts/j1c77g4l_VetterTec%20Workshop%20with%20Tube%20bundle%20dryers%20cmyk300.jpg',
    image: 'https://customer-assets.emergentagent.com/job_4992c407-209d-4ffe-8201-36194c9609e6/artifacts/u8jqbtnv_VetterTec%20Workshop_Overview%20with%20screw%20conveyor%20in%20front.JPG',
    website: null
  },
  {
    id: 4,
    name: 'TCEM',
    status: 'In Portfolio',
    investor: 'Moret Industries Group',
    investmentStrategy: 'Buyout',
    geographies: ['France'],
    geographicPresence: 'Le Havre (France)',
    sectors: ['Energy & Utilities', 'Manufacturing', 'Services'],
    activityType: 'Manufacturing, Services',
    description: "TCEM is a designer, manufacturer and distributor of equipment for the fabrication and maintenance of tubular heat exchangers and boilers, valves, piping and pipelines, as well as level control and safety solutions for energy applications. Formerly the technical division of TROUVAY & CAUVIN, TCEM is recognized globally for its expertise in level measurement through its MIP brand, covering design, production and distribution of level instrumentation.",
    descriptionFr: "TCEM est un concepteur, fabricant et distributeur d'équipements pour la fabrication et la maintenance d'échangeurs thermiques tubulaires et de chaudières, de vannes, de tuyauteries et de canalisations, ainsi que de solutions de contrôle de niveau et de sécurité pour les applications énergétiques. Anciennement la division technique de TROUVAY & CAUVIN, TCEM est reconnu mondialement pour son expertise en mesure de niveau à travers sa marque MIP, couvrant la conception, la production et la distribution d'instrumentation de niveau.",
    geographicPresenceFr: 'Le Havre (France)',
    activityTypeFr: 'Fabrication, Services',
    logo: null,
    image: 'https://customer-assets.emergentagent.com/job_69649056-df98-4ca6-bbd3-c5d20803d8d9/artifacts/ehkuvbsr_image00007.jpeg',
    website: 'https://www.tcem.fr/'
  },
  {
    id: 5,
    name: 'DeSmet Engineers & Contractors',
    status: 'Partially Realised',
    investor: 'Moret Industries Group',
    investmentStrategy: 'Growth',
    geographies: ['Belgium', 'India'],
    geographicPresence: 'Mont-Saint-Guibert (Belgium), Chennai (India)',
    sectors: ['Food & Agriculture', 'Bio-based industries', 'Energy (renewables, alternative fuels)'],
    activityType: 'Engineering',
    description: "De Smet Engineers & Contractors (DSEC), founded in 1990 within the DeSmet Group, is an Engineering and General Contracting company acting as process integrator, specialised in the agro-industrial fields such as Sugar & Ethanol, VegOils & Derivatives, Bio-based Industries, Alt Protein & Ingredients and Agro-nutrients. DSEC's engineering skills combined with its agro-industrial processes expertise have been selected by major players for developing large-scale industrial plants from laboratory scale, pilot units and demonstration plants. DSEC has completed over 500 projects across 65 countries.",
    descriptionFr: "De Smet Engineers & Contractors (DSEC), fondée en 1990 au sein du groupe DeSmet, est une société d'ingénierie et de contractualisation générale agissant en tant qu'intégrateur de procédés, spécialisée dans les domaines agro-industriels tels que le sucre et l'éthanol, les huiles végétales et dérivés, les industries biosourcées, les protéines alternatives et ingrédients, et les agro-nutriments. Les compétences en ingénierie de DSEC, combinées à son expertise en procédés agro-industriels, ont été retenues par des acteurs majeurs pour le développement de grandes installations industrielles, des unités pilotes et de démonstration à l'échelle du laboratoire. DSEC a réalisé plus de 500 projets dans 65 pays.",
    geographicPresenceFr: 'Mont-Saint-Guibert (Belgique), Chennai (Inde)',
    activityTypeFr: 'Ingénierie',
    logo: 'https://customer-assets.emergentagent.com/job_69649056-df98-4ca6-bbd3-c5d20803d8d9/artifacts/ehkuvbsr_image00007.jpeg',
    image: 'https://customer-assets.emergentagent.com/job_69649056-df98-4ca6-bbd3-c5d20803d8d9/artifacts/z3o66ynt_image00009.jpeg',
    website: null
  },
  {
    id: 6,
    name: 'Opeo Consulting',
    status: 'In Portfolio',
    investor: 'Ocean Peak Capital Partners',
    investmentStrategy: 'Growth',
    geographies: ['France'],
    geographicPresence: 'Paris (France)',
    sectors: ['Aerospace & Defence', 'Healthcare', 'Luxury', 'Process Industries'],
    activityType: 'Services (Consulting)',
    description: "Opeo, founded in 2012, is an operations consulting firm focused on the Industrials space. Opeo brings unique capabilities in operational transformation, working with leading industrial companies across a variety of sectors to support them as they transform their companies holistically, from the executive committee to the workshop.",
    descriptionFr: "Opeo, fondée en 2012, est un cabinet de conseil en opérations axé sur le secteur industriel. Opeo apporte des compétences uniques en transformation opérationnelle, travaillant avec des entreprises industrielles de premier plan dans une variété de secteurs pour les accompagner dans la transformation globale de leurs entreprises, du comité de direction jusqu'à l'atelier.",
    geographicPresenceFr: 'Paris (France)',
    activityTypeFr: 'Services (Conseil)',
    logo: 'https://customer-assets.emergentagent.com/job_69649056-df98-4ca6-bbd3-c5d20803d8d9/artifacts/4hrln9d3_image00010.jpeg',
    image: 'https://customer-assets.emergentagent.com/job_4992c407-209d-4ffe-8201-36194c9609e6/artifacts/l60db05f_VetterTec%20Workshop_Mechanic%20at%20Screw%20Press%20basket.JPG',
    website: null
  }
];

// Extract unique values for filters
const getAllSectors = () => {
  const sectors = new Set();
  companiesData.forEach(company => {
    company.sectors.forEach(sector => sectors.add(sector));
  });
  return Array.from(sectors).sort();
};

const getAllGeographies = () => {
  const geos = new Set();
  companiesData.forEach(company => {
    company.geographies.forEach(geo => geos.add(geo));
  });
  return Array.from(geos).sort();
};

const getAllInvestmentStrategies = () => {
  const strategies = new Set();
  companiesData.forEach(company => strategies.add(company.investmentStrategy));
  return Array.from(strategies);
};

const getAllInvestors = () => {
  const investors = new Set();
  companiesData.forEach(company => investors.add(company.investor));
  return Array.from(investors);
};

export const Investments = () => {
  const { language } = useLanguage();
  const bannerRef = useRef(null);
  const imageRef = useRef(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  
  const [filters, setFilters] = useState({
    sectors: [],
    geographies: [],
    status: 'all',
    investmentStrategies: [],
    investors: []
  });

  const filterOptions = {
    sectors: getAllSectors(),
    geographies: getAllGeographies(),
    investmentStrategies: getAllInvestmentStrategies(),
    investors: getAllInvestors()
  };

  useEffect(() => {
    if (imageRef.current) {
      gsap.to(imageRef.current, {
        yPercent: 20,
        ease: 'none',
        scrollTrigger: {
          trigger: bannerRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1
        }
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const handleCheckboxChange = (category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(item => item !== value)
        : [...prev[category], value]
    }));
  };

  const handleStatusChange = (status) => {
    setFilters(prev => ({ ...prev, status }));
  };

  const clearAllFilters = () => {
    setFilters({
      sectors: [],
      geographies: [],
      status: 'all',
      investmentStrategies: [],
      investors: []
    });
  };

  // Filter companies based on selected filters
  const filteredCompanies = companiesData.filter(company => {
    // Status filter
    if (filters.status !== 'all') {
      if (filters.status === 'portfolio' && company.status !== 'In Portfolio') return false;
      if (filters.status === 'partiallyRealised' && company.status !== 'Partially Realised') return false;
      if (filters.status === 'realised' && company.status !== 'Realised') return false;
    }

    // Sector filter
    if (filters.sectors.length > 0) {
      const hasMatchingSector = company.sectors.some(sector => filters.sectors.includes(sector));
      if (!hasMatchingSector) return false;
    }

    // Geography filter
    if (filters.geographies.length > 0) {
      const hasMatchingGeo = company.geographies.some(geo => filters.geographies.includes(geo));
      if (!hasMatchingGeo) return false;
    }

    // Investment Strategy filter
    if (filters.investmentStrategies.length > 0) {
      if (!filters.investmentStrategies.includes(company.investmentStrategy)) return false;
    }

    // Investor filter
    if (filters.investors.length > 0) {
      if (!filters.investors.includes(company.investor)) return false;
    }

    return true;
  });

  const ctaButtons = [
    { label: language === 'fr' ? 'INVESTIR AVEC NOUS' : 'INVEST WITH US', link: '/contact?type=invest' },
    { label: language === 'fr' ? 'TRAVAILLER AVEC NOUS' : 'WORK WITH US', link: '/contact?type=work' },
    { label: language === 'fr' ? 'DEVENIR PARTENAIRE' : 'PARTNER WITH US', link: '/contact?type=partner' }
  ];

  return (
    <div className="min-h-screen bg-technical-white">
      {/* Floating CTA Buttons */}
      <div className="fixed right-8 top-1/3 z-40 flex flex-col gap-3 hidden lg:flex">
        {ctaButtons.map((btn, idx) => (
          <Link
            key={idx}
            to={btn.link}
            className="text-white text-xs font-bold tracking-wider px-5 py-3 bg-light-blue hover:bg-abyssal-blue transition-all duration-300 shadow-lg whitespace-nowrap"
            style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif", letterSpacing: '1px', fontSize: '10px' }}
          >
            {btn.label}
          </Link>
        ))}
      </div>

      {/* Hero Banner */}
      <section 
        ref={bannerRef}
        className="relative h-[500px] flex flex-col overflow-hidden"
        style={{ backgroundColor: '#0a1f35' }}
      >
        <div 
          ref={imageRef}
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{
            backgroundImage: 'url(/hero-companies.jpg)'
          }}
        />
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(10, 31, 53, 0.3)' }} />
        
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4">
          <h1 
            className="text-5xl lg:text-6xl text-white mb-8 text-center animate-fade-in-slow" 
            style={{ 
              fontFamily: "'Bank Gothic', 'Impact', sans-serif", 
              fontWeight: 900,
              letterSpacing: '0.02em',
              textTransform: 'uppercase'
            }}
          >
            {language === 'fr' ? 'INVESTISSEMENTS' : 'INVESTMENTS'}
          </h1>
          <p
            className="text-xl lg:text-2xl text-white text-center max-w-4xl mx-auto font-bold"
            style={{
              fontFamily: "'Eurostile', 'Arial', sans-serif",
              lineHeight: 1.6
            }}
          >
            {language === 'fr'
              ? "Un portefeuille concentré d'entreprises industrielles européennes, conçu pour une création de valeur durable."
              : "A concentrated portfolio of European industrial businesses, built for long-term value creation."}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12">
            
            {/* Left Sidebar - Filters with new Investment Strategy and Investor filters */}
            <div className="lg:w-72 flex-shrink-0">
              <div className="bg-white shadow-xl lg:sticky lg:top-32">
                {/* Filter Header */}
                <div className="p-6 border-b border-gray-100">
                  <h2 
                    className="text-lg text-abyssal-blue"
                    style={{ fontFamily: "'Novecento Sans Wide', sans-serif", fontWeight: 600, letterSpacing: '0.1em' }}
                  >
                    {language === 'fr' ? 'FILTRES' : 'FILTERS'}
                  </h2>
                </div>

                <div className="p-6 space-y-8">
                  {/* INVESTMENT STRATEGY - New Filter */}
                  <div>
                    <h3 
                      className="text-xs text-steel-grey uppercase tracking-widest mb-4"
                      style={{ fontFamily: "'Novecento Sans Wide', sans-serif" }}
                    >
                      {language === 'fr' ? 'Stratégie d\'investissement' : 'Investment Strategy'}
                    </h3>
                    <div className="space-y-2">
                      {['Buyout', 'Growth', 'Special Situations', 'Venture'].map((strategy) => (
                        <label
                          key={strategy}
                          className="flex items-center gap-3 cursor-pointer group py-1"
                          onClick={() => handleCheckboxChange('investmentStrategies', strategy)}
                        >
                          <div className={`w-4 h-4 border-2 flex items-center justify-center transition-all ${
                            filters.investmentStrategies.includes(strategy)
                              ? 'border-light-blue bg-light-blue'
                              : 'border-steel-grey/40 group-hover:border-light-blue'
                          }`}>
                            {filters.investmentStrategies.includes(strategy) && (
                              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                              </svg>
                            )}
                          </div>
                          <span className="text-sm text-steel-grey group-hover:text-abyssal-blue transition-colors">
                            {language === 'fr' ? strategyTranslations[strategy] : strategy}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gray-100"></div>

                  {/* INVESTOR - New Filter */}
                  <div>
                    <h3 
                      className="text-xs text-steel-grey uppercase tracking-widest mb-4"
                      style={{ fontFamily: "'Novecento Sans Wide', sans-serif" }}
                    >
                      {language === 'fr' ? 'Investisseur' : 'Investor'}
                    </h3>
                    <div className="space-y-2">
                      {filterOptions.investors.map((investor) => (
                        <label
                          key={investor}
                          className="flex items-center gap-3 cursor-pointer group py-1"
                          onClick={() => handleCheckboxChange('investors', investor)}
                        >
                          <div className={`w-4 h-4 border-2 flex items-center justify-center transition-all ${
                            filters.investors.includes(investor)
                              ? 'border-light-blue bg-light-blue'
                              : 'border-steel-grey/40 group-hover:border-light-blue'
                          }`}>
                            {filters.investors.includes(investor) && (
                              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                              </svg>
                            )}
                          </div>
                          <span className="text-sm text-steel-grey group-hover:text-abyssal-blue transition-colors">
                            {investor}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gray-100"></div>

                  {/* STATUS */}
                  <div>
                    <h3 
                      className="text-xs text-steel-grey uppercase tracking-widest mb-4"
                      style={{ fontFamily: "'Novecento Sans Wide', sans-serif" }}
                    >
                      {language === 'fr' ? 'Statut' : 'Status'}
                    </h3>
                    <div className="space-y-3">
                      {[
                        { value: 'all', label: language === 'fr' ? 'Tous' : 'All' },
                        { value: 'portfolio', label: language === 'fr' ? 'En portefeuille' : 'In Portfolio' },
                        { value: 'partiallyRealised', label: language === 'fr' ? 'Partiellement réalisé' : 'Partially Realised' },
                        { value: 'realised', label: language === 'fr' ? 'Réalisé' : 'Realised' }
                      ].map((option) => (
                        <label
                          key={option.value}
                          className="flex items-center gap-3 cursor-pointer group"
                          onClick={() => handleStatusChange(option.value)}
                        >
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${
                            filters.status === option.value 
                              ? 'border-light-blue bg-light-blue' 
                              : 'border-steel-grey/40 group-hover:border-light-blue'
                          }`}>
                            {filters.status === option.value && (
                              <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                            )}
                          </div>
                          <span className={`text-sm transition-colors ${
                            filters.status === option.value ? 'text-abyssal-blue font-medium' : 'text-steel-grey'
                          }`}>
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gray-100"></div>

                  {/* SECTOR */}
                  <div>
                    <h3 
                      className="text-xs text-steel-grey uppercase tracking-widest mb-4"
                      style={{ fontFamily: "'Novecento Sans Wide', sans-serif" }}
                    >
                      {language === 'fr' ? 'Secteur' : 'Sector'}
                    </h3>
                    <div className="space-y-2 max-h-44 overflow-y-auto pr-2 blue-scrollbar">
                      {['Aerospace & Defence', 'Energy & Utilities', 'Food & Agriculture', 'Healthcare'].map((sector) => (
                        <label
                          key={sector}
                          className="flex items-center gap-3 cursor-pointer group py-1"
                          onClick={() => handleCheckboxChange('sectors', sector)}
                        >
                          <div className={`w-4 h-4 border-2 flex items-center justify-center transition-all ${
                            filters.sectors.includes(sector)
                              ? 'border-light-blue bg-light-blue'
                              : 'border-steel-grey/40 group-hover:border-light-blue'
                          }`}>
                            {filters.sectors.includes(sector) && (
                              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                              </svg>
                            )}
                          </div>
                          <span className="text-sm text-steel-grey group-hover:text-abyssal-blue transition-colors">
                            {language === 'fr' ? (sectorTranslations[sector] || sector) : sector}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gray-100"></div>

                  {/* GEOGRAPHY */}
                  <div>
                    <h3 
                      className="text-xs text-steel-grey uppercase tracking-widest mb-4"
                      style={{ fontFamily: "'Novecento Sans Wide', sans-serif" }}
                    >
                      {language === 'fr' ? 'Géographie' : 'Geography'}
                    </h3>
                    <div className="relative space-y-2 max-h-36 overflow-y-auto blue-scrollbar border border-light-blue/40 rounded p-2" style={{ paddingRight: '14px' }}>
                      {filterOptions.geographies.map((geo) => (
                        <label
                          key={geo}
                          className="flex items-center gap-3 cursor-pointer group py-1"
                          onClick={() => handleCheckboxChange('geographies', geo)}
                        >
                          <div className={`w-4 h-4 border-2 flex items-center justify-center transition-all ${
                            filters.geographies.includes(geo)
                              ? 'border-light-blue bg-light-blue'
                              : 'border-steel-grey/40 group-hover:border-light-blue'
                          }`}>
                            {filters.geographies.includes(geo) && (
                              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                              </svg>
                            )}
                          </div>
                          <span className="text-sm text-steel-grey group-hover:text-abyssal-blue transition-colors">
                            {language === 'fr' ? (geoTranslations[geo] || geo) : geo}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Clear Button */}
                <div className="p-6 border-t border-gray-100">
                  <button
                    onClick={clearAllFilters}
                    className="w-full py-3 text-xs uppercase tracking-widest text-steel-grey hover:text-white hover:bg-abyssal-blue border border-steel-grey/30 hover:border-abyssal-blue transition-all duration-300"
                    style={{ fontFamily: "'Novecento Sans Wide', sans-serif" }}
                  >
                    {language === 'fr' ? 'Effacer les filtres' : 'Clear Filters'}
                  </button>
                </div>
              </div>
            </div>

            {/* Right Content - Company Tiles */}
            <div className="flex-1">
              {/* Company Tiles Grid */}
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
                {filteredCompanies.map((company) => (
                  <div 
                    key={company.id}
                    className="group bg-white shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden cursor-pointer"
                    onClick={() => setSelectedCompany(selectedCompany === company.id ? null : company.id)}
                  >
                    {/* Image */}
                    <div className="relative h-72 overflow-hidden">
                      <img 
                        src={company.image} 
                        alt={company.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-abyssal-blue/80 via-abyssal-blue/20 to-transparent"></div>
                      
                      {/* Status Badge */}
                      <div className="absolute top-4 left-4">
                        <span 
                          className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider backdrop-blur-sm ${
                            company.status === 'In Portfolio' 
                              ? 'bg-white/90 text-abyssal-blue' 
                              : 'bg-amber-400/90 text-abyssal-blue'
                          }`}
                          style={{ fontFamily: "'Novecento Sans Wide', sans-serif", fontSize: '9px', letterSpacing: '0.1em' }}
                        >
                          {language === 'fr' ? statusTranslations[company.status] : company.status}
                        </span>
                      </div>

                      {/* Investment Strategy Badge */}
                      <div className="absolute top-4 right-4">
                        <span
                          className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider backdrop-blur-sm bg-light-blue/90 text-white"
                          style={{ fontFamily: "'Novecento Sans Wide', sans-serif", fontSize: '9px', letterSpacing: '0.1em' }}
                        >
                          {language === 'fr' ? strategyTranslations[company.investmentStrategy] : company.investmentStrategy}
                        </span>
                      </div>
                      
                      {/* Company Name on Image */}
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 
                          className="text-xl text-white font-semibold"
                          style={{ fontFamily: "'Novecento Sans Wide', sans-serif", letterSpacing: '0.02em' }}
                        >
                          {company.name}
                        </h3>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-5">
                      {/* Quick Info */}
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-steel-grey">
                          <Building2 size={14} className="text-light-blue" />
                          <span>{company.investor}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-steel-grey">
                          <Briefcase size={14} className="text-light-blue" />
                          <span>{language === 'fr' ? strategyTranslations[company.investmentStrategy] : company.investmentStrategy}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-steel-grey">
                          <MapPin size={14} className="text-light-blue" />
                          <span>{language === 'fr' ? company.geographies.map(g => geoTranslations[g] || g).join(', ') : company.geographies.join(', ')}</span>
                        </div>
                      </div>
                      
                      {/* Sectors */}
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {company.sectors.map((sector, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-technical-white text-steel-grey text-xs"
                            style={{ fontSize: '10px' }}
                          >
                            {language === 'fr' ? (sectorTranslations[sector] || sector) : sector}
                          </span>
                        ))}
                      </div>
                      
                      {/* View More */}
                      <div className="flex items-center gap-1 text-light-blue text-sm font-medium group-hover:gap-2 transition-all">
                        <span style={{ fontFamily: "'Eurostile', sans-serif" }}>
                          {language === 'fr' ? 'En savoir plus' : 'Learn more'}
                        </span>
                        <ChevronRight size={16} />
                      </div>
                    </div>
                    
                    {/* Expanded Content */}
                    {selectedCompany === company.id && (
                      <div className="px-5 pb-5 border-t border-gray-100 bg-technical-white/50">
                        <div className="pt-5 space-y-4">
                          <div>
                            <h4 
                              className="text-xs text-abyssal-blue uppercase tracking-wider mb-2"
                              style={{ fontFamily: "'Novecento Sans Wide', sans-serif" }}
                            >
                              {language === 'fr' ? 'Présence' : 'Presence'}
                            </h4>
                            <p className="text-sm text-steel-grey">{language === 'fr' ? company.geographicPresenceFr : company.geographicPresence}</p>
                          </div>
                          <div>
                            <h4 
                              className="text-xs text-abyssal-blue uppercase tracking-wider mb-2"
                              style={{ fontFamily: "'Novecento Sans Wide', sans-serif" }}
                            >
                              {language === 'fr' ? 'Activité' : 'Activity'}
                            </h4>
                            <p className="text-sm text-steel-grey">{language === 'fr' ? company.activityTypeFr : company.activityType}</p>
                          </div>
                          <div>
                            <h4 
                              className="text-xs text-abyssal-blue uppercase tracking-wider mb-2"
                              style={{ fontFamily: "'Novecento Sans Wide', sans-serif" }}
                            >
                              Description
                            </h4>
                            <p 
                              className="text-sm text-steel-grey leading-relaxed"
                              style={{ fontFamily: "'Eurostile', sans-serif" }}
                            >
                              {language === 'fr' ? company.descriptionFr : company.description}
                            </p>
                          </div>
                          {company.website && (
                            <div>
                              <a 
                                href={company.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-light-blue hover:text-abyssal-blue transition-colors text-sm"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink size={14} />
                                {language === 'fr' ? 'Visiter le site' : 'Visit website'}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Empty State */}
              {filteredCompanies.length === 0 && (
                <div className="text-center py-20 bg-white shadow-lg">
                  <div className="text-steel-grey/30 mb-4">
                    <Factory size={64} className="mx-auto" />
                  </div>
                  <p className="text-xl text-steel-grey mb-2" style={{ fontFamily: "'Novecento Sans Wide', sans-serif" }}>
                    {language === 'fr' ? 'Aucune société trouvée' : 'No companies found'}
                  </p>
                  <p className="text-sm text-steel-grey/60">
                    {language === 'fr' ? 'Essayez d\'ajuster vos filtres' : 'Try adjusting your filters'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
